import React, { useContext, useEffect } from "react";
import styles from "./styles/Unauthorized.module.css";
import useWindowSize from "../../../hooks/useWindowSize";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import DataContext from "../../../context/DataContext";
import NavButton from "../../../components/ui/navbutton/NavButton";
import useCurrebtBU from "../../../hooks/useCurrentBU";
/**
 * Unauthorized page
 */
const Unauthorized = () => {
    const { t } = useTranslation(); // Hook for handling translations using i18next.

    const {
        currentCustom, // {Object} - The current client customizations
        serviceData, // {Function} - Fetch service data
        isServiceDataAvailable, // {Boolean} - Indicates if the service data is available
        currentBU, // {String} - The current business user
        JWTToken,
    } = useContext(DataContext);

    const windowSize = useWindowSize(); // Custom hook to get the current window size.
    const { bUser } = useParams(); // Extracts 'bUser' parameter from URL.
    useCurrebtBU(bUser);

    // useEffect to fetch service data if 'bUser' is provided and service data is not available.
    useEffect(() => {
        bUser &&
            JWTToken &&
            (!isServiceDataAvailable || bUser !== currentBU) &&
            serviceData(bUser);
    }, [bUser, isServiceDataAvailable, JWTToken]);

    return (
        isServiceDataAvailable && (
            <>
                <article
                    className={`${styles.image401_page_container} main-page-container flex-container`}
                >
                    <section
                        className={`${styles.image401_main_container} main-container flex-container`}
                    >
                        <div
                            className={`${styles.image401_media_container} v-flex-container`}
                        >
                            <h1 className={`${styles.image401_text} `}>
                                {t(`unauthorized`)}
                            </h1>
                            <div
                                className={`${styles.image401_button_container}`}
                            >
                                <NavButton
                                    body={t(`home page`)}
                                    link={`/${bUser}/pos`}
                                />
                            </div>
                        </div>
                        <div className={`${styles.image401}`}>
                            <svg
                                viewBox="0 0 329 432.8"
                                width={
                                    windowSize.width <= 480 ? "15rem" : "20rem"
                                }
                            >
                                <use href="#image401"></use>
                            </svg>
                        </div>
                    </section>
                    {/* 401 image svg */}
                    <svg
                        className={`display-non`}
                        xmlns="http://www.w3.org/2000/svg"
                        version="1.1"
                    >
                        <symbol
                            id="image401"
                            data-name="image401"
                            viewBox="0 0 329 432.8"
                        >
                            <path
                                d="M226.1,394.1v-209h-12.7v208.8c-10.7,2.5-18.6,13.3-18.6,25.2h49.2c0-11.7-7.6-22.3-17.9-25h0Z"
                                fill="#031b26"
                                fillRule="evenodd"
                            />
                            <path
                                d="M321.5,98.2l-50.9,88.2h-101.8l-50.9-88.2L168.8,10h101.8l50.9,88.2h0Z"
                                fill={`${currentCustom?.color?.current}`}
                            />
                            <path
                                d="M289.2,53.6h6.6l-25.2-43.7h-101.8l-50.9,88.2,21.3,37c33-49.3,87.8-81.6,149.9-81.6h.1Z"
                                fill="#fff"
                                fillOpacity=".5"
                            />
                            <path
                                d="M266.2,19.8l-.6-1h-91.7l-45.9,79.4,45.3,78.4.6,1h91.7l45.9-79.4-45.3-78.4h0ZM263.3,173.7h-87.1l-43.5-75.4,43.5-75.4h87.1l43.5,75.4-43.5,75.4h0Z"
                                fill="#fff"
                            />
                            <polygon
                                points="75.4 276.1 109.7 276.1 151.4 395.4 33.8 395.4 75.4 276.1"
                                fill="#fff"
                            />
                            <g>
                                <path
                                    d="M169,405.8h-14.3c0-.6-.1-1.2-.3-1.8l-3-8.6H33.8l-3,8.6c-.2.6-.3,1.2-.3,1.8h-14.3c-4.7,0-8.6,3.8-8.6,8.6s3.8,8.6,8.6,8.6h152.9c4.7,0,8.6-3.8,8.6-8.6s-3.8-8.6-8.6-8.6h-.1,0Z"
                                    fill={`${currentCustom?.color?.current}`}
                                />
                                <polygon
                                    points="7.5 414.4 7.5 414.4 7.5 414.4 7.5 414.4"
                                    fill={`${currentCustom?.color?.current}`}
                                />
                                <polygon
                                    points="177.6 414.4 177.6 414.4 177.6 414.4 177.6 414.4"
                                    fill={`${currentCustom?.color?.current}`}
                                />
                                <path
                                    d="M98.2,243.1c-1.9-5.4-9.5-5.4-11.3,0l-11.5,33h34.3l-11.5-33Z"
                                    fill={`${currentCustom?.color?.current}`}
                                />
                                <polygon
                                    points="123.8 316.3 61.3 316.3 47.6 355.9 137.6 355.9 123.8 316.3"
                                    fill={`${currentCustom?.color?.current}`}
                                />
                            </g>
                            <path
                                d="M103.8,276.1l-10.1-36.9c-.7-.1-1.5-.1-2.2,0l2.5,36.9s9.8,0,9.8,0ZM114.8,316.3h-18l2.7,39.5h26.1s-10.8-39.5-10.8-39.5ZM102.9,405.8l-.7-10.4h34.2l2.9,10.4h-36.4Z"
                                fill="#fff"
                                fillOpacity=".5"
                                fillRule="evenodd"
                            />
                            <path
                                d="M226.4,126.2c-3.7,0-6.9-.5-9.6-1.5-2.7-1.1-5-2.5-6.8-4.4-1.7-1.8-3.1-4-4-6.5s-1.5-5.1-1.6-7.9c0-1.4-.1-2.9-.2-4.7v-5.4c0-1.8.1-3.5.2-4.9.2-2.8.7-5.4,1.6-7.8,1-2.5,2.4-4.6,4.1-6.4,1.8-1.8,4.1-3.3,6.8-4.3s5.9-1.5,9.5-1.5,6.8.5,9.4,1.5c2.7,1,4.9,2.5,6.7,4.3s3.3,3.9,4.2,6.4c1,2.4,1.5,5,1.6,7.8.1,1.4.2,3.1.2,4.9v5.4c0,1.7,0,3.3-.2,4.7-.1,2.8-.6,5.5-1.6,7.9-.9,2.5-2.3,4.6-4.1,6.5-1.7,1.8-4,3.3-6.7,4.4-2.7,1-5.9,1.5-9.6,1.5h.1ZM226.4,115c2.7,0,4.6-.8,5.8-2.5s1.9-4.1,1.9-7c.1-1.5.2-3,.2-4.7v-4.9c0-1.6,0-3.1-.2-4.5,0-2.8-.7-5.1-1.9-6.8-1.2-1.7-3.1-2.6-5.8-2.7-2.7,0-4.6.9-5.8,2.7-1.2,1.7-1.8,4-1.9,6.8,0,1.4-.1,2.9-.2,4.5v4.9c0,1.6.1,3.2.2,4.7.1,2.9.8,5.2,2,7,1.2,1.7,3.1,2.5,5.7,2.5h0Z"
                                fill="#fff"
                            />
                            <path
                                d="M176.3,125.4c-.5,0-1-.2-1.4-.5-.4-.4-.5-.8-.5-1.4v-8.7h-23.9c-.5,0-1-.2-1.4-.5-.4-.4-.5-.8-.5-1.4v-7.3c0-.5,0-.9.2-1.2.2-.4.4-.8.6-1.2l22.4-30.3c.7-.9,1.6-1.3,2.8-1.3h11.1c.6,0,1,.2,1.4.5.4.4.6.8.6,1.4v29.8h6.8c.6,0,1.1.2,1.5.6s.5.8.5,1.3v7.6c0,.6-.2,1-.6,1.4s-.8.5-1.3.5h-6.8v8.7c0,.6-.2,1-.6,1.4s-.8.5-1.4.5h-9.5ZM161.7,103.9h12.9v-18.1l-12.9,18.1Z"
                                fill="#fff"
                            />
                            <path
                                d="M263.7,83.3h-5.6c-1.1,0-2-.9-2-2v-7.8c0-1.1.9-2,2-2h17.1c1.1,0,2,.9,2,2v49.8c0,1.1-.9,2-2,2h-9.6c-1.1,0-2-.9-2-2v-40Z"
                                fill="#fff"
                            />
                        </symbol>
                    </svg>
                </article>
            </>
        )
    );
};

export default Unauthorized;
